import { useRef, useState } from 'react';
import { DebouncedInput } from './DebounceInput';
import { useKeyPress } from 'hooks/useKeyPress';
import { outlinedInputClasses } from '@mui/material';
import styled from '@emotion/styled';

export type SearchProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onClear?: () => void;
  searchOpen?: boolean;
  onSearchOpenChange?: (open: boolean) => void;
  enableKeyboardShortcuts?: boolean;
  fullWidth?: boolean;
  id?: string;
  'data-testid'?: string;
};

/**
 * Search component with expandable/collapsible animation and keyboard shortcuts. Part of the Delogue design system.
 * @param value - The current search value
 * @param onChange - Callback function when the search value changes
 * @param placeholder - Placeholder text (default: 'Search')
 * @param onClear - Callback function when the search is cleared
 * @param searchOpen - Controlled state for whether the search is open/expanded
 * @param onSearchOpenChange - Callback function when the search open state changes
 * @param enableKeyboardShortcuts - Whether to enable keyboard shortcuts (default: true)
 * @param id - HTML id attribute for the input element
 * @param data-testid - Test ID for testing purposes
 */
export const Search = ({
  value,
  onChange,
  placeholder = 'Search',
  onClear,
  searchOpen: controlledSearchOpen,
  onSearchOpenChange,
  enableKeyboardShortcuts = true,
  fullWidth = false,
  id,
  'data-testid': dataTestId,
}: SearchProps) => {
  const [internalSearchOpen, setInternalSearchOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  // Use controlled or uncontrolled state
  const searchOpen = controlledSearchOpen !== undefined ? controlledSearchOpen : internalSearchOpen;
  const setSearchOpen = (open: boolean) => {
    if (onSearchOpenChange) {
      onSearchOpenChange(open);
    } else {
      setInternalSearchOpen(open);
    }
  };

  const handleClickAway = () => {
    if (!value) {
      setSearchOpen(false);
      searchRef?.current?.blur();
    }
  };

  const handleFocus = () => {
    searchRef?.current?.focus();
  };

  const handleClear = () => {
    setSearchOpen(false);
    if (onClear) {
      onClear();
    } else {
      onChange('');
    }
  };

  const isSearchOpenOrValue = searchOpen || Boolean(value);
  const onClick = () => {
    if (!isSearchOpenOrValue) {
      setSearchOpen(true);
      handleFocus();
    }
  };

  useKeyPress('ctrl+f', () => {
    if (enableKeyboardShortcuts && !searchOpen) {
      setSearchOpen(true);
      handleFocus();
    }
  });

  useKeyPress('Escape', () => {
    if (enableKeyboardShortcuts) {
      if (searchOpen && !value) {
        handleClear();
      }
      searchRef?.current?.blur();
    }
  });

  return (
    <SearchBox open={isSearchOpenOrValue} fullWidth={fullWidth}>
      <StyledDebouncedInput
        ref={searchRef}
        data-testid={dataTestId}
        id={id}
        isSearchOpenOrValue={isSearchOpenOrValue}
        fullWidth={fullWidth}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        onClear={handleClear}
        onBlur={handleClickAway}
        onClick={onClick}
      />
    </SearchBox>
  );
};

const StyledDebouncedInput = styled(DebouncedInput, {
  shouldForwardProp: p => p !== 'isSearchOpenOrValue',
})<{
  isSearchOpenOrValue?: boolean;
  fullWidth?: boolean;
}>`
  &.MuiFormControl-root {
    svg {
      min-width: 20px;
      height: 20px;
    }
    border: none;
    margin: 0;
    outline: none;
    position: absolute;
    /* Center in the 56px SearchBox: hosts like the table toolbar (44px, overflow hidden)
       center this box, so a top-pinned input gets its top edge clipped. */
    top: 50%;
    transform: translateY(-50%);
    ${p =>
      p.fullWidth && p.isSearchOpenOrValue
        ? `
    left: 0;
    right: 0;
    width: 100%;
  `
        : `
    right: 0;
  `}
    input {
      padding-left: unset;
    }
    max-width: 32px;
    padding-inline: 4px;
    transition: max-width 0.3s ease;
    ${p => p.isSearchOpenOrValue && `max-width: 100%;`}
    &:focus-within {
      max-width: 100%;
    }
    & .${outlinedInputClasses.root} {
      transition: background-color 1000ms ease-out;
      ${p =>
        !p.isSearchOpenOrValue &&
        `
        padding-left: 0;
        background-color: transparent;
        cursor: pointer;
      `}
      & .${outlinedInputClasses.notchedOutline} {
        transition:
          border 1000ms ease-out,
          box-shadow 1000ms ease-out;
        ${p => !p.isSearchOpenOrValue && `border: none;`}
      }
      &.${outlinedInputClasses.focused} {
        padding-left: 14px;
      }
    }
  }
`;

const SearchBox = styled.div<{ open: boolean; fullWidth?: boolean }>`
  position: relative;
  width: ${p => (p.open ? (p.fullWidth ? '100%' : '320px') : '30px')};
  height: 56px;
  overflow: hidden;
  display: flex;
  align-items: center;
  transition: width 0.3s ease;
`;
